import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  convertToCSV,
  downloadAsCSV,
  fetcher,
  mapKeyToObject,
} from '@/lib/utils'
import {
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  FileSpreadsheet,
  RefreshCw,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useDatabaseContext } from '../../context'
import { AdminExport } from '@/components/icons'

export const ExportCsv = () => {
  const t = useTranslations('database')

  const [isExporting, setIsExporting] = useState(false)

  const [showSuccess, setShowSuccess] = useState(false)

  const { project, tableName, selectedTable } = useDatabaseContext()

  const usersCSVColumns: {
    name: string
    type: string
    nullable: boolean
  }[] = [
    { name: 'email', type: 'text', nullable: false },
    { name: 'user_id', type: 'uuid', nullable: false },
    { name: 'login_time', type: 'date', nullable: false },
  ]

  const showSuccessAnimation = () => {
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 1500)
  }

  const exportDataAsCsv = async () => {
    try {
      if (!project || !tableName) return
      setIsExporting(true)

      const url = `/api/me/project/${project._id}/table/${tableName}/export`
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch data')

      const result = await response.json()
      const data = result.data || []
      // Get columns based on whether it's the users table or a custom table
      const columns =
        tableName === 'users' ? usersCSVColumns : selectedTable?.columns || []

      const csvContent = convertToCSV(data, columns)
      downloadAsCSV(csvContent, `${tableName}.csv`)
      showSuccessAnimation()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export data')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant="default"
      size="sm"
      onClick={exportDataAsCsv}
      disabled={isExporting}
      className="bg-transparent hover:bg-transparent"
    >
      {isExporting ? (
        <LoaderCircle className="w-4 h-4 animate-spin" />
      ) : showSuccess ? (
        <div className="flex items-center gap-1">
          <svg
            className="w-4 h-4 text-green-500 animate-scale-check"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <title>Done</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span>{t('done')}</span>
        </div>
      ) : (
        <div className="flex items-center gap-x-[4px]">
          <AdminExport className="w-4 h-4 text-white" />
          <div className="text-white text-sm font-medium">{t('export')}</div>
        </div>
      )}
    </Button>
  )
}
